import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckInIcon,
  Download as DownloadIcon,
  ArrowBack as BackIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Cancel as CancelledIcon,
  EventAvailable as ConfirmedIcon,
  HourglassEmpty as PendingIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import rsvpService from '../../services/rsvpService';
import eventService from '../../services/eventService';

const Attendees = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({});
  const [checkInDialog, setCheckInDialog] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRSVP, setSelectedRSVP] = useState(null);
  const [viewDetailsDialog, setViewDetailsDialog] = useState(false);
  const [viewingRSVP, setViewingRSVP] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loadingQR, setLoadingQR] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingRSVP, setEditingRSVP] = useState(null);
  const [editFormData, setEditFormData] = useState({ status: '', adminNotes: '' });
  const [updating, setUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEvent();
    fetchAttendees();
  }, [eventId, statusFilter, searchQuery]);

  const fetchEvent = async () => {
    try {
      const response = await eventService.getEventById(eventId);
      if (response.success) {
        setEvent(response.data.event);
      }
    } catch (err) {
      console.error('Failed to fetch event:', err);
    }
  };

  const fetchAttendees = async () => {
    try {
      setLoading(true);
      setError('');

      const filters = {
        page: page + 1,
        limit: rowsPerPage
      };

      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      if (searchQuery) {
        filters.search = searchQuery;
      }

      const response = await rsvpService.getEventAttendees(eventId, filters);

      if (response.success) {
        setAttendees(response.data.attendees || []);

        // Convert statusBreakdown to stats format
        const statusBreakdown = response.data.statusBreakdown || {};
        const statsData = {
          total: response.data.pagination?.total || 0,
          confirmed: statusBreakdown.confirmed || 0,
          attended: statusBreakdown.attended || 0,
          pending: statusBreakdown.pending || 0,
          cancelled: statusBreakdown.cancelled || 0,
          waitlisted: statusBreakdown.waitlisted || 0
        };
        setStats(statsData);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch attendees');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event, newValue) => {
    setStatusFilter(newValue);
    setPage(0);
  };

  const handleOpenCheckInDialog = (attendee) => {
    setSelectedAttendee(attendee);
    setCheckInDialog(true);
  };

  const handleCloseCheckInDialog = () => {
    setCheckInDialog(false);
    setSelectedAttendee(null);
  };

  const handleCheckIn = async () => {
    if (!selectedAttendee) return;

    try {
      setCheckingIn(true);
      const response = await rsvpService.checkInAttendee(selectedAttendee._id);

      if (response.success) {
        // Update the attendee in the list
        setAttendees(prevAttendees =>
          prevAttendees.map(att =>
            att._id === selectedAttendee._id
              ? { ...att, status: 'attended', checkInTime: new Date().toISOString() }
              : att
          )
        );
        handleCloseCheckInDialog();
        fetchAttendees(); // Refresh to update stats
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to check in attendee');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const filters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }

      await rsvpService.exportAttendees(eventId, filters);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to export attendees');
    }
  };

  const handleMenuOpen = (event, rsvp) => {
    setAnchorEl(event.currentTarget);
    setSelectedRSVP(rsvp);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRSVP(null);
  };

  const handleViewDetails = async (rsvp) => {
    try {
      const response = await rsvpService.getRSVPById(rsvp._id);
      if (response.success) {
        setViewingRSVP(response.data.rsvp);
        setViewDetailsDialog(true);

        // Fetch QR code if RSVP is confirmed or attended
        if (response.data.rsvp.status === 'confirmed' || response.data.rsvp.status === 'attended') {
          setLoadingQR(true);
          try {
            const qrResponse = await rsvpService.getQRCode(rsvp._id);
            if (qrResponse.success) {
              setQrCodeData(qrResponse.data);
            }
          } catch (qrErr) {
            console.error('Failed to load QR code:', qrErr);
          } finally {
            setLoadingQR(false);
          }
        }
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load RSVP details');
    }
    handleMenuClose();
  };

  const handleCloseViewDetails = () => {
    setViewDetailsDialog(false);
    setViewingRSVP(null);
    setQrCodeData(null);
  };

  const handleDownloadPDF = async () => {
    if (!viewingRSVP) return;

    try {
      // Get the dialog content element
      const dialogContent = document.querySelector('#rsvp-details-content');
      if (!dialogContent) {
        alert('Unable to generate PDF. Please try again.');
        return;
      }

      // Hide elements with pdf-exclude class
      const excludeElements = dialogContent.querySelectorAll('.pdf-exclude');
      excludeElements.forEach(el => {
        el.style.display = 'none';
      });

      // Apply compact styles temporarily
      const originalStyles = {
        padding: dialogContent.style.padding,
        margin: dialogContent.style.margin
      };

      dialogContent.style.padding = '10px';
      dialogContent.style.margin = '0';

      // Apply compact styles to all boxes
      const boxes = dialogContent.querySelectorAll('.MuiBox-root');
      const boxStyles = [];
      boxes.forEach(box => {
        boxStyles.push({
          padding: box.style.padding,
          marginBottom: box.style.marginBottom
        });
        box.style.padding = '8px';
        box.style.marginBottom = '8px';
      });

      // Resize QR code for PDF
      const qrImage = dialogContent.querySelector('img');
      let originalQRStyles = null;
      if (qrImage) {
        originalQRStyles = {
          width: qrImage.style.width,
          height: qrImage.style.height
        };
        qrImage.style.width = '250px';
        qrImage.style.height = '250px';
      }

      // Capture the content as canvas
      const canvas = await html2canvas(dialogContent, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      // Restore original styles
      dialogContent.style.padding = originalStyles.padding;
      dialogContent.style.margin = originalStyles.margin;
      boxes.forEach((box, index) => {
        box.style.padding = boxStyles[index].padding;
        box.style.marginBottom = boxStyles[index].marginBottom;
      });
      if (qrImage && originalQRStyles) {
        qrImage.style.width = originalQRStyles.width;
        qrImage.style.height = originalQRStyles.height;
      }
      excludeElements.forEach(el => {
        el.style.display = '';
      });

      // Calculate PDF dimensions
      const imgWidth = 210; // A4 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

      // Generate filename
      const attendeeName = `${viewingRSVP.attendeeInfo?.firstName || ''}_${viewingRSVP.attendeeInfo?.lastName || ''}`.replace(/\s+/g, '_');
      const eventName = event?.eventName?.replace(/\s+/g, '_') || 'Event';
      const filename = `RSVP_${attendeeName}_${eventName}.pdf`;

      // Download the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');

      // Ensure styles are restored even on error
      const dialogContent = document.querySelector('#rsvp-details-content');
      if (dialogContent) {
        const excludeElements = dialogContent.querySelectorAll('.pdf-exclude');
        excludeElements.forEach(el => {
          el.style.display = '';
        });
      }
    }
  };

  const handleOpenEditDialog = (rsvp) => {
    setEditingRSVP(rsvp);
    setEditFormData({
      status: rsvp.status,
      adminNotes: rsvp.adminNotes || ''
    });
    setEditDialog(true);
    handleMenuClose();
  };

  const handleCloseEditDialog = () => {
    setEditDialog(false);
    setEditingRSVP(null);
    setEditFormData({ status: '', adminNotes: '' });
  };

  const handleUpdateRSVP = async () => {
    if (!editingRSVP) return;

    try {
      setUpdating(true);
      const response = await rsvpService.updateRSVP(editingRSVP._id, editFormData);

      if (response.success) {
        // Update the attendee in the list
        setAttendees(prevAttendees =>
          prevAttendees.map(att =>
            att._id === editingRSVP._id
              ? { ...att, status: editFormData.status, adminNotes: editFormData.adminNotes }
              : att
          )
        );
        handleCloseEditDialog();
        fetchAttendees(); // Refresh to update stats
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update RSVP');
    } finally {
      setUpdating(false);
    }
  };

  const handleSelectAttendee = (rsvpId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(rsvpId)) {
      newSelected.delete(rsvpId);
    } else {
      newSelected.add(rsvpId);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === attendees.length && attendees.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(attendees.map(a => a._id)));
    }
  };

  const handleOpenBulkDeleteDialog = () => {
    if (selectedIds.size === 0) {
      alert('Please select at least one attendee');
      return;
    }
    setBulkDeleteDialog(true);
  };

  const handleCloseBulkDeleteDialog = () => {
    setBulkDeleteDialog(false);
  };

  const handleBulkDelete = async () => {
    try {
      setDeleting(true);
      const response = await rsvpService.bulkDeleteRSVPs(
        Array.from(selectedIds),
        eventId
      );

      if (response.success) {
        setSelectedIds(new Set());
        setBulkDeleteDialog(false);
        fetchAttendees(); // Refresh list and stats
        fetchEvent(); // Refresh event data
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete attendees');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const formatDateTime = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <ConfirmedIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'attended':
        return <CheckInIcon fontSize="small" />;
      case 'cancelled':
        return <CancelledIcon fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading && !attendees.length) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="xl">
        {/* Header */}
        <Box mb={4}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/admin/events')}
            sx={{ mb: 2 }}
          >
            Back to Events
          </Button>

          <Box display="flex" justifyContent="space-between" alignItems="start">
            <Box>
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                Event Attendees
              </Typography>
              {event && (
                <Typography variant="h6" color="text.secondary">
                  {event.eventName}
                </Typography>
              )}
            </Box>

            <Box display="flex" gap={2}>
              {selectedIds.size > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleOpenBulkDeleteDialog}
                >
                  Delete Selected ({selectedIds.size})
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchAttendees}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            },
            gap: 3,
            mb: 3,
          }}
        >
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total RSVPs
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.confirmed || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Confirmed
                  </Typography>
                </Box>
                <ConfirmedIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {stats.attended || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Checked In
                  </Typography>
                </Box>
                <CheckInIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.pending || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
                <PendingIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Filters and Search */}
        <Paper sx={{ mb: 3 }}>
          <Box p={2}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: { xs: 'stretch', md: 'center' },
              }}
            >
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <TextField
                  fullWidth
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' } }}>
                <Tabs
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="All" value="all" />
                  <Tab label="Confirmed" value="confirmed" />
                  <Tab label="Pending" value="pending" />
                  <Tab label="Attended" value="attended" />
                  <Tab label="Cancelled" value="cancelled" />
                </Tabs>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Attendees Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: selectedIds.size > 0 ? 'action.hover' : 'transparent' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && selectedIds.size < attendees.length}
                    checked={selectedIds.size > 0 && selectedIds.size === attendees.length && attendees.length > 0}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Attendee</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell>Phone</TableCell>
                {/* Dynamic custom field columns */}
                {event?.customFields && event.customFields.map((field) => (
                  <TableCell key={field.fieldName}>{field.fieldLabel}</TableCell>
                ))}
                <TableCell>Status</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Check-in Time</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10 + (event?.customFields?.length || 0)} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : attendees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10 + (event?.customFields?.length || 0)} align="center" sx={{ py: 8 }}>
                    <PersonAddIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No attendees found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                attendees.map((rsvp) => (
                  <TableRow key={rsvp._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(rsvp._id)}
                        onChange={() => handleSelectAttendee(rsvp._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Click to view details">
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          onClick={() => handleViewDetails(rsvp)}
                          sx={{
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'primary.dark'
                            }
                          }}
                        >
                          {rsvp.attendeeInfo?.firstName} {rsvp.attendeeInfo?.lastName}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {rsvp.attendeeInfo?.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {rsvp.attendeeInfo?.jobTitle || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {rsvp.attendeeInfo?.phone || '-'}
                      </Typography>
                    </TableCell>

                    {/* Dynamic custom field columns */}
                    {event?.customFields && event.customFields.map((field) => (
                      <TableCell key={field.fieldName}>
                        <Typography variant="body2" color="text.secondary">
                          {rsvp.attendeeInfo?.customFields?.[field.fieldName] || '-'}
                        </Typography>
                      </TableCell>
                    ))}

                    <TableCell align="center">
                      <Chip
                        label={rsvp.additionalGuests || 0}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(rsvp.status)}
                        label={rsvpService.formatStatus(rsvp.status)}
                        color={rsvpService.getStatusColor(rsvp.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(rsvp.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {rsvp.checkInTime ? (
                        <Typography variant="body2" color="success.main">
                          {formatDateTime(rsvp.checkInTime)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {(rsvp.status === 'confirmed' || rsvp.status === 'pending') && !rsvp.checkInTime && (
                        <Tooltip title="Check In">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenCheckInDialog(rsvp)}
                          >
                            <CheckInIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="More options">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, rsvp)}
                        >
                          <MoreIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={stats.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleOpenEditDialog(selectedRSVP)}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit RSVP
          </MenuItem>
        </Menu>

      {/* Check-in Confirmation Dialog */}
      <Dialog open={checkInDialog} onClose={handleCloseCheckInDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Check In Attendee</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Confirm check-in for:
          </Typography>
          <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
            <Typography variant="h6">
              {selectedAttendee?.attendeeInfo?.firstName} {selectedAttendee?.attendeeInfo?.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedAttendee?.attendeeInfo?.email}
            </Typography>
            {selectedAttendee?.additionalGuests > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                +{selectedAttendee.additionalGuests} additional{' '}
                {selectedAttendee.additionalGuests === 1 ? 'guest' : 'guests'}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseCheckInDialog} disabled={checkingIn}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCheckIn}
            disabled={checkingIn}
            startIcon={checkingIn ? <CircularProgress size={16} /> : <CheckInIcon />}
          >
            {checkingIn ? 'Checking In...' : 'Check In'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog} onClose={handleCloseViewDetails} maxWidth="md" fullWidth>
        <DialogTitle>RSVP Details</DialogTitle>
        <DialogContent>
          {viewingRSVP && (
            <Box id="rsvp-details-content">
              {/* Event Details Section - Full Width at Top */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.05) 100%)',
                  p: 1.5,
                  borderRadius: 1.5,
                  mb: 1.5,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}
                className="print-section"
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 1, fontSize: '0.95rem', color: 'primary.main' }}
                >
                  Event Details
                </Typography>
                <Divider sx={{ mb: 1.5, borderColor: 'primary.light' }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr' },
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                    >
                      Event Name
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                      {event?.eventName || '-'}
                    </Typography>
                  </Box>
                  {event?.dates?.[0]?.startDate && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                      >
                        Event Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {formatDateTime(event.dates[0].startDate)}
                      </Typography>
                    </Box>
                  )}
                  {event?.location?.venue && (
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                      >
                        Location
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {event.location.venue}
                        {event.location.address && `, ${event.location.address}`}
                        {event.location.city && `, ${event.location.city}`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* 2-Column Layout: Attendee Info (Left) + QR Code (Right) */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                {/* Left Column - Attendee Information */}
                <Box
                  sx={{
                    bgcolor: 'grey.50',
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    borderLeft: '3px solid',
                    borderLeftColor: 'success.main'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 1, fontSize: '0.95rem', color: 'success.main' }}
                  >
                    Attendee Information
                  </Typography>
                  <Divider sx={{ mb: 1.5, borderColor: 'grey.300' }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                      >
                        Full Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {viewingRSVP.attendeeInfo?.firstName} {viewingRSVP.attendeeInfo?.lastName}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                      >
                        Email
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {viewingRSVP.attendeeInfo?.email}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                      >
                        Job Title
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {viewingRSVP.attendeeInfo?.jobTitle || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                      >
                        Phone
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {viewingRSVP.attendeeInfo?.phone || '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ fontSize: '0.7rem', fontWeight: 600, mb: 0.3 }}
                      >
                        Additional Guests
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {viewingRSVP.additionalGuests || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Right Column - QR Code */}
                {(viewingRSVP.status === 'confirmed' || viewingRSVP.status === 'attended') && (
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(102, 126, 234, 0.05) 100%)',
                      p: 1.5,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'info.light',
                      boxShadow: '0 2px 4px rgba(33, 150, 243, 0.15)',
                      borderLeft: '3px solid',
                      borderLeftColor: 'info.main'
                    }}
                    className="print-section"
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{ mb: 1, fontSize: '0.95rem', color: 'info.main' }}
                    >
                      Check-in QR Code
                    </Typography>
                    <Divider sx={{ mb: 1.5, borderColor: 'info.light' }} />
                    {loadingQR ? (
                      <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : qrCodeData?.qrCode ? (
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <Box
                          component="img"
                          src={qrCodeData.qrCode}
                          alt="QR Code"
                          sx={{
                            width: 300,
                            height: 300,
                            border: '3px solid',
                            borderColor: 'info.main',
                            borderRadius: 1.5,
                            p: 1,
                            bgcolor: 'white',
                            boxShadow: '0 2px 8px rgba(33, 150, 243, 0.2)'
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ fontSize: '0.7rem', fontStyle: 'italic', mt: 0.5 }}
                        >
                          Scan this QR code at the event for check-in
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        textAlign="center"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        QR code not available
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Registration Details - Hidden from PDF */}
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }} className="pdf-exclude">
                <Typography variant="h6" gutterBottom>
                  Registration Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Status
                    </Typography>
                    <Chip
                      icon={getStatusIcon(viewingRSVP.status)}
                      label={rsvpService.formatStatus(viewingRSVP.status)}
                      color={rsvpService.getStatusColor(viewingRSVP.status)}
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Registration Date
                    </Typography>
                    <Typography variant="body1">
                      {viewingRSVP.createdAt && formatDateTime(viewingRSVP.createdAt)}
                    </Typography>
                  </Box>
                  {viewingRSVP.checkInTime && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Check-in Time
                      </Typography>
                      <Typography variant="body1" color="success.main">
                        {formatDateTime(viewingRSVP.checkInTime)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {viewingRSVP.specialRequirements && (
                <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Special Requirements
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewingRSVP.specialRequirements}
                  </Typography>
                </Box>
              )}

              {viewingRSVP.adminNotes && (
                <Box sx={{ bgcolor: 'warning.lighter', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Admin Notes
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewingRSVP.adminNotes}
                  </Typography>
                </Box>
              )}

              {viewingRSVP.cancellationReason && (
                <Box sx={{ bgcolor: 'error.lighter', p: 2, borderRadius: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    Cancellation Reason
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewingRSVP.cancellationReason}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseViewDetails}>Close</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit RSVP Dialog */}
      <Dialog open={editDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit RSVP</DialogTitle>
        <DialogContent>
          {editingRSVP && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Attendee: {editingRSVP.attendeeInfo?.firstName} {editingRSVP.attendeeInfo?.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                {editingRSVP.attendeeInfo?.email}
              </Typography>

              <TextField
                select
                fullWidth
                label="Status"
                value={editFormData.status}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                sx={{ mb: 3 }}
                SelectProps={{
                  native: true
                }}
              >
                <option value="pending">Pending Approval</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
                <option value="attended">Attended</option>
                <option value="waitlisted">Waitlisted</option>
              </TextField>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Admin Notes"
                value={editFormData.adminNotes}
                onChange={(e) => setEditFormData({ ...editFormData, adminNotes: e.target.value })}
                placeholder="Add internal notes about this attendee..."
                helperText="These notes are only visible to admins"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseEditDialog} disabled={updating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateRSVP}
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : null}
          >
            {updating ? 'Updating...' : 'Update RSVP'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialog}
        onClose={() => setBulkDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6">
              Delete {selectedIds.size} Attendee{selectedIds.size !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to permanently delete {selectedIds.size} attendee{selectedIds.size !== 1 ? 's' : ''}? This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The following attendees will be deleted:
          </Typography>
          <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
            {attendees
              .filter(a => selectedIds.has(a._id))
              .map(a => (
                <Box key={a._id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight="medium">
                    {a.attendeeInfo?.firstName} {a.attendeeInfo?.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {a.attendeeInfo?.email}
                  </Typography>
                </Box>
              ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setBulkDeleteDialog(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Box>
    </>
  );
};

export default Attendees;
